import { useCallback, useEffect, useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import { Icon, Colors, Text } from '@mercator-gcp/grout';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Link } from 'components/elements/Link';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import BasePageWithTopNav from 'components/layout/TopNavigationLayout';
import PageMetadata from 'components/PageMetadata';
import { useDashboardItemSelection } from 'components/widgets/Dashboard/useDashboardItemSelection';
import { useCollections, useCollectionsShared } from 'hooks/useCollections';
import { useGroupConfigurations } from 'hooks/useGroupConfig';
import { useUser } from 'state/auth';
import { useFlyoutNavigation } from 'state/flyoutNavigation';
import { trackSecondaryNavigation } from 'utils/analytics';
import {
  SecondaryNavigationClickActions,
  SecondaryNavigationClickLocations,
} from 'utils/analytics/constants';
import { NEW_GROUP_ID } from 'utils/constants';
import { scrollBarPrefixRemoval } from 'utils/styles';

const flexRow = {
  container: {
    alignItems: 'start',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },
  left: {
    height: '100%',
    alignItems: 'start',
    zIndex: '100',
    backgroundColor: 'brand.army-green-darkest',
    maxWidth: '240px',
    ...scrollBarPrefixRemoval,
  },
  content: {
    justifyContent: 'space-between',
    alignItems: 'center',
    opacity: '20%',
  },
};

const boxContainer = {
  width: '2px',
  height: '15px',
  backgroundColor: '#D9D9D9',
  transform: 'rotate(-45deg)',
};

const companyNameStyle = {
  paddingY: '35px',
};

const COMPANY_NAME_DISPLAY_OVERRIDES: Record<string, string> = {
  Bror: 'BROR',
};

const boxContainerStart = {
  width: '2px',
  height: '15px',
  backgroundColor: '#D9D9D9',
  transform: 'rotate(225deg)',
};

const boxContainerEnd = {
  width: '2px',
  height: '15px',
  backgroundColor: '#D9D9D9',
  transform: 'rotate(135deg)',
};

const menu = {
  container: {
    overflowY: 'hidden',
  },
  links: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderTop: `1px solid ${Colors.ArmyGreen900}`,
    '&:last-of-type': {
      borderBottom: `1px solid ${Colors.ArmyGreen900}`,
    },
    '&.chakra-link': {
      textDecoration: 'none',
      '&:hover': {
        color: 'brand.army-green',
      },
    },
  },
};

const contentStyle = {
  flex: 1,
  width: '85%',
  alignItems: 'start',
  justifyContent: 'center',
  height: '100%',
  overflowY: 'auto',
};

const menuLabelStyle = {
  columnGap: 'padding_16',
};

const groupRow = (isActive: boolean) => ({
  width: '100%',
  py: 'padding_16',
  px: 'padding_24',
  backgroundColor: isActive ? 'brand.army-green-dark' : 'transparent',
  gap: '8px',
  color: Colors.TextWhite,
});

const dashboardPath = {
  projects: '/dashboard/projects',
  companies: '/dashboard/companies',
  pinned: '/dashboard/pinned',
  shared: '/dashboard/shared',
};

export const DashboardPage = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user } = useUser();
  const { data: usersGroups } = useGroupConfigurations({ userId: String(user?.id) });
  const { getCollectionsList } = useCollections();
  const { data: collectionList } = getCollectionsList;
  const { result: collectionsShared } = useCollectionsShared();
  const { updateFlyoutNavigationStatus } = useFlyoutNavigation();
  const { handleItemSelectionSideEffect } = useDashboardItemSelection();
  const { id: urlId } = useParams();

  useEffect(() => {
    // set flyout navigation as default open when loading the dashboard
    updateFlyoutNavigationStatus(true);
  }, [updateFlyoutNavigationStatus]);

  const routes = useMemo(
    () => [
      {
        path: dashboardPath.projects,
        label: 'Saved Searches',
        iconName: Icon.Name.ProjectLookup,
        count: (usersGroups ?? []).filter((group) => group.type === 1).length,
        clickAction: SecondaryNavigationClickActions.DASHBOARD__SAVED_SEARCHES,
      },
      {
        path: dashboardPath.companies,
        label: 'Company Groups',
        iconName: Icon.Name.CompanyBuilding,
        count: (usersGroups ?? []).filter((group) => group.type === 2).length,
        clickAction: SecondaryNavigationClickActions.DASHBOARD__COMPANY_GROUPS,
      },
      {
        path: dashboardPath.pinned,
        label: 'Lead Lists',
        iconName: Icon.Name.Star,
        count: collectionList?.length,
        clickAction: SecondaryNavigationClickActions.DASHBOARD__SAVED_PROJECTS,
      },
      {
        path: dashboardPath.shared,
        label: 'Shared Lists',
        iconName: Icon.Name.AddUser,
        count: collectionsShared?.data.length,
        clickAction: SecondaryNavigationClickActions.DASHBOARD__SHARED_PROJECTS,
      },
    ],
    [collectionList?.length, collectionsShared?.data.length, usersGroups],
  );

  const isActive = useCallback(
    (path: string) => pathname.startsWith(path) && pathname.endsWith(path),
    [pathname],
  );

  const isId = (val: string) => !!(val === NEW_GROUP_ID || Number.parseInt(val, 10));

  // Redirect to the first item if no item selected
  useEffect(() => {
    const type = pathname.split('/')[2];
    if (
      !['projects', 'pinned', 'companies', 'pinned-companies', 'shared'].includes(type) &&
      !isId(urlId || '')
    ) {
      navigate(routes[0].path, { replace: true });
    }
  }, [navigate, pathname, routes]);

  return (
    <BasePageWithTopNav>
      <PageMetadata title="Saved Searches" />
      <FlexRow sx={flexRow.container}>
        <FlexRow.Left sx={flexRow.left}>
          <FlexColumn>
            <FlexColumn padding="16px" sx={{ width: '240px' }}>
              <FlexRow sx={flexRow.content}>
                <Box sx={boxContainer} />
                <Box sx={boxContainer} />
              </FlexRow>
              <FlexRow justifyContent="center" alignItems="center">
                <Text.Lead color={Colors.White} align="center" sx={companyNameStyle}>
                  {COMPANY_NAME_DISPLAY_OVERRIDES[user?.company_name ?? ''] ??
                    user?.company_name}
                </Text.Lead>
              </FlexRow>
              <FlexRow sx={flexRow.content}>
                <Box sx={boxContainerStart} />
                <Box sx={boxContainerEnd} />
              </FlexRow>
            </FlexColumn>
            <FlexColumn sx={menu.container}>
              {routes.map(({ path, label, iconName, count, clickAction, ...props }) => (
                <Link
                  {...props}
                  key={`dashboard-option-${path}`}
                  to={path}
                  onClick={() => {
                    handleItemSelectionSideEffect();
                    updateFlyoutNavigationStatus(true);
                    trackSecondaryNavigation({
                      clickAction,
                      clickLocation:
                        SecondaryNavigationClickLocations.DASHBOARD__LEFT_STATIC_NAV,
                      clickText: label,
                    });
                  }}
                  sx={menu.links}
                >
                  <FlexRow sx={groupRow(isActive(path))}>
                    <FlexRow sx={menuLabelStyle}>
                      <Icon name={iconName} size={24} />
                      <Text.BodyBold>{label}</Text.BodyBold>
                    </FlexRow>
                    <Text>{count}</Text>
                  </FlexRow>
                </Link>
              ))}
            </FlexColumn>
          </FlexColumn>
        </FlexRow.Left>
        <FlexRow sx={contentStyle}>
          <Outlet />
        </FlexRow>
      </FlexRow>
    </BasePageWithTopNav>
  );
};
